import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import ContactHeader from './components/ContactHeader';
import ContactTabs from './components/ContactTabs';
import ContactDetailsTab from './components/ContactDetailsTab';
import ContactHistoryTab from './components/ContactHistoryTab';
import ContactNotesTab from './components/ContactNotesTab';
import QuickMessageComposer from './components/QuickMessageComposer';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const ContactDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get contact from navigation state or use mock data
  const [contact, setContact] = useState(location?.state?.contact || {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@techcorp.com",
    phone: "+1 (555) 123-4567",
    company: "TechCorp Solutions",
    jobTitle: "Senior Product Manager",
    birthday: "1990-03-15",
    location: "San Francisco, CA",
    website: "https://sarahjohnson.dev",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    tags: ["Work", "Tech", "Product"],
    notes: `Met Sarah at the annual tech conference in San Francisco. She's very knowledgeable about product management and user experience design. 

Key points from our conversation:
- Currently working on AI-powered analytics tools
- Interested in collaboration opportunities
- Prefers email communication
- Available for meetings on Tuesdays and Thursdays

Follow-up: Send her the product roadmap document we discussed.`,
    createdAt: "2023-08-15T10:30:00Z",
    updatedAt: "2024-08-10T14:20:00Z",
    messageCount: 12,
    viewCount: 8,
    lastContactDate: "2024-08-05T16:45:00Z"
  });

  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mock message history
  const messageHistory = [
    {
      id: 1,
      type: 'birthday',
      message: `Happy Birthday Sarah! 🎉 Hope you have a wonderful day filled with joy and celebration!`,
      timestamp: new Date('2024-03-15T09:00:00'),
      status: 'delivered',
      method: 'email'
    },
    {
      id: 2,
      type: 'random',
      message: `Hi Sarah! Just wanted to check in and see how the new product launch is going. Hope everything is going well!`,
      timestamp: new Date('2024-02-28T14:30:00'),
      status: 'read',
      method: 'email'
    },
    {
      id: 3,
      type: 'manual',
      message: `Thanks for the great meeting today! I've attached the product roadmap document we discussed. Looking forward to our collaboration.`,
      timestamp: new Date('2024-02-20T16:45:00'),status: 'read',method: 'email'
    }
  ];

  useEffect(() => {
    // If no contact data, redirect to contact list
    if (!contact?.id) {
      navigate('/contact-list');
    }
  }, [contact, navigate]);

  const handleSaveContact = async (updatedContact) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setContact(updatedContact);
      console.log('Contact saved:', updatedContact);
    } catch (error) {
      console.error('Error saving contact:', error);
      throw error;
    }
  };

  const handleSaveNotes = async (notes) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setContact(prev => ({ ...prev, notes }));
      console.log('Notes saved:', notes);
    } catch (error) {
      console.error('Error saving notes:', error);
      throw error;
    }
  };

  const handleSendMessage = async (messageData) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Message sent:', messageData);
      
      // Update message count
      setContact(prev => ({
        ...prev,
        messageCount: (prev?.messageCount || 0) + 1,
        lastContactDate: new Date()?.toISOString()
      }));
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  const handleDeleteContact = async () => {
    setIsDeleting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Contact deleted:', contact?.id);
      navigate('/contact-list');
    } catch (error) {
      console.error('Error deleting contact:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCall = () => {
    if (contact?.phone) {
      window.open(`tel:${contact?.phone}`);
    }
  };

  const handleEmail = () => {
    if (contact?.email) {
      window.open(`mailto:${contact?.email}`);
    }
  };

  const handleMessage = (type = 'general') => {
    setShowMessageComposer(true);
  };

  const handleEdit = () => {
    navigate('/add-edit-contact', { 
      state: { 
        contact, 
        isEditing: true 
      } 
    });
  };

  const handleClose = () => {
    navigate(-1);
  };

  const customBreadcrumbs = [
    { label: 'Dashboard', path: '/dashboard', icon: 'Home' },
    { label: 'Contacts', path: '/contact-list', icon: 'Users' },
    { label: contact?.name, path: '/contact-details', icon: 'User' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <BottomTabNavigation />
      {/* Main Content */}
      <div className="lg:ml-60 min-h-screen">
        {/* Breadcrumbs */}
        <NavigationBreadcrumbs 
          customBreadcrumbs={customBreadcrumbs}
          onBackClick={handleClose}
        />

        {/* Contact Header */}
        <ContactHeader
          contact={contact}
          onEdit={handleEdit}
          onDelete={() => setShowDeleteModal(true)}
          onCall={handleCall}
          onMessage={handleMessage}
          onEmail={handleEmail}
          onClose={handleClose}
        />

        {/* Tab Navigation */}
        <ContactTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          messageCount={messageHistory?.length}
          notesCount={contact?.notes ? 1 : 0}
        />

        {/* Tab Content */}
        <div className="pb-20 lg:pb-8">
          {activeTab === 'details' && (
            <ContactDetailsTab
              contact={contact}
              onSave={handleSaveContact}
              isEditing={isEditing}
              onToggleEdit={() => setIsEditing(!isEditing)}
            />
          )}

          {activeTab === 'history' && (
            <ContactHistoryTab
              contact={contact}
              messageHistory={messageHistory}
            />
          )}

          {activeTab === 'notes' && (
            <ContactNotesTab
              contact={contact}
              onSaveNotes={handleSaveNotes}
            />
          )}
        </div>
      </div>
      {/* Quick Message Composer */}
      {showMessageComposer && (
        <QuickMessageComposer
          contact={contact}
          onSendMessage={handleSendMessage}
          onClose={() => setShowMessageComposer(false)}
        />
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmationModal
          contact={contact}
          onConfirm={handleDeleteContact}
          onCancel={() => setShowDeleteModal(false)}
          isDeleting={isDeleting}
        />
      )}
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation />
      {/* Floating Action Button - Mobile */}
      <div className="lg:hidden fixed bottom-20 right-4 z-200">
        <button
          onClick={() => setShowMessageComposer(true)}
          className="w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-modal flex items-center justify-center hover-lift transition-micro"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ContactDetailsPage;